import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { staffApi } from "@client/api/staff";
import { StaffShell } from "@client/components/StaffShell";
import { Badge } from "@client/components/ui/badge";
import { Button } from "@client/components/ui/button";
import { useConfirm } from "@client/components/confirm-dialog";

export const Route = createFileRoute("/_authenticated/exams/")({
  component: ExamsPage,
});

function ExamsPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();

  const { data: exams } = useQuery({
    queryKey: ["exams"],
    queryFn: staffApi.exams,
  });

  const deleteExamMut = useMutation({
    mutationFn: (id: string) => staffApi.deleteExam(id),
    onSuccess: () => {
      toast.success("Exam deleted successfully");
      qc.invalidateQueries({ queryKey: ["exams"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const clearAllMut = useMutation({
    mutationFn: () => staffApi.clearAllExams(),
    onSuccess: () => {
      toast.success("All exams deleted");
      qc.invalidateQueries({ queryKey: ["exams"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  async function handleDeleteExam(id: string, name: string) {
    if (
      await confirm({
        title: "Delete exam?",
        description: `Delete "${name}"? This action cannot be undone.`,
        confirmText: "Delete",
        destructive: true,
      })
    ) {
      deleteExamMut.mutate(id);
    }
  }

  async function handleClearAll() {
    if (
      await confirm({
        title: "Delete all exams?",
        description:
          "Active sessions will prevent deletion. This action cannot be undone.",
        confirmText: "Delete all",
        destructive: true,
      })
    ) {
      clearAllMut.mutate();
    }
  }

  return (
    <StaffShell
      title="Exams"
      subtitle="Frozen question sets with generated versions"
      actions={
        <div className="flex items-center gap-2">
          {exams && exams.length > 0 ? (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleClearAll}
              disabled={clearAllMut.isPending}
            >
              {clearAllMut.isPending ? "Clearing..." : "Delete all"}
            </Button>
          ) : null}
          <Button asChild size="sm">
            <Link to="/exams/new">Create exam</Link>
          </Button>
        </div>
      }
    >
      <div className="grid gap-4 md:grid-cols-2">
        {(exams ?? []).map((e) => (
          <div key={e.id} className="panel p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="hall-heading text-lg font-bold text-foreground">
                  {e.name}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {(e.categories as { name: string } | null)?.name ?? "—"}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{e.status}</Badge>
                <Button
                  asChild
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs"
                >
                  <Link to="/exams/$examId/edit" params={{ examId: e.id }}>
                    Edit
                  </Link>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => handleDeleteExam(e.id, e.name)}
                  disabled={deleteExamMut.isPending}
                >
                  Delete
                </Button>
              </div>
            </div>
            <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-muted-foreground">Questions</dt>
                <dd className="font-semibold">
                  {(e.exam_questions as unknown as { count: number }[])?.[0]
                    ?.count ?? 0}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Versions</dt>
                <dd className="font-semibold">
                  {(e.exam_versions as unknown as { count: number }[])?.[0]
                    ?.count ?? 0}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Time / question</dt>
                <dd className="font-semibold">{e.time_per_question}s</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Passing score</dt>
                <dd className="font-semibold">{e.passing_score}%</dd>
              </div>
            </dl>
          </div>
        ))}
        {!exams?.length ? (
          <p className="text-sm text-muted-foreground">No exams created yet.</p>
        ) : null}
      </div>
    </StaffShell>
  );
}
